import React, { useState } from "react";
import { StoryElement, ImageGalleryElement, WithLazy, Link } from "@quintype/components";
import { object, number, string, func, array } from "prop-types";
import get from "lodash/get";

import LightBox from "../../molecules/LightBox";
import FileAttachment from "../../molecules/FileAttachment";
import AlsoRead from "../../molecules/AlsoRead";
import ExternalLinkIcon from "../../atoms/icons/external-link";
import ImageNumber from "../../atoms/ImageNumber";
import CustomImageGalleryElement from "./ImageGallery";
import CustomQuoteElement from "./CustomQuoteElement";
import DefaultCardImage from "../../atoms/CardImage/default-card-image.svg";

import "./story-card.m.css";
import assetify from "@quintype/framework/assetify";
import Polltype from "./PolltypeElement";

const StoryCard = ({ story, card, onCarouselEvent, publisherAttributes, imageElements = {} }) => {
  const [galleryItemIndex, setGalleryItemIndex] = useState(0);
  const [showPopup, setPopup] = useState({});
  const imagePosition = get(card, ["metadata", "attributes", "imageposition", 0], "");
  const togglePopup = (id, isPopupOpen, index = 0) => {
    setPopup({ [id]: isPopupOpen });
    setGalleryItemIndex(index);

    const element = document.getElementsByTagName("body")[0];
    isPopupOpen ? (element.style.overflow = "hidden") : (element.style.overflow = "");

    if (document.fullscreenElement) {
      document
        .exitFullscreen()
        .then(() => console.log("exited form Full Screen mode"))
        .catch((err) => console.error(err));
    }
  };

  const templates = {
    "also-read": AlsoRead,
    file: FileAttachment,
  };

  const imageAlign = () => {
    let style;
    switch (imagePosition) {
      case "Left Align":
        style = "left-align";
        break;

      case "Right Align":
        style = "right-align";
        break;

      case "Full Width":
        style = "full-width";
        break;

      default:
        style = "";
        break;
    }
    return style;
  };

  const langWrapper = `${publisherAttributes.lang}-wrapper`;

  return (
    <div
      styleName={`wrapper ${
        story["story-template"] === "photo" || story["story-template"] === "gallery" ? "photo-wrapper" : ""
      } ${imageAlign()} ${langWrapper} ${
        story["story-template"] === "text" && story["author-name"] === "migrator" ? "left-align" : ""
      } ${publisherAttributes.lang === "en" ? "eng-wrapper" : ""}`}
    >
      {card["story-elements"] &&
        card["story-elements"].map((element, index) => {
          const imageElementStyle =
            element.type === "image" &&
            card["story-elements"].length > index + 1 &&
            card["story-elements"][index + 1].type === "text" &&
            !card["story-elements"][index + 1].subtype
              ? imageAlign()
              : "";

          const defaultStoryElement = () => {
            const imageWidth = global.deviceWidth <= 876 || typeof window === "undefined" ? [360] : [700];
            if (element.type === "image") {
              return (
                <WithLazy margin="50px">
                  {() => (
                    <div>
                      {story && (story["story-template"] === "photo" || story["story-template"] === "gallery") && (
                        <ImageNumber element={element} imageElements={imageElements} />
                      )}

                      <StoryElement
                        renderTemplates={templates}
                        story={story}
                        key={element.id}
                        element={element}
                        externalLink={true}
                        onClick={(e) => onCarouselEvent(e, element)}
                        imageWidths={imagePosition || story["author-name"] === "migrator" ? [250] : [imageWidth]}
                      />
                    </div>
                  )}
                </WithLazy>
              );
            } else {
              return (
                <StoryElement
                  renderTemplates={templates}
                  story={story}
                  key={element.id}
                  element={element}
                  externalLink={true}
                  onClick={(e) => onCarouselEvent(e, element)}
                  imageWidths={imagePosition || story["author-name"] === "migrator" ? [250] : [imageWidth]}
                />
              );
            }
          };

          const addWithLazyWrapper = () => {
            if (element.type === "image" && typeof window === "undefined") {
              return (
                <img className="picture default-image" src={`${assetify(DefaultCardImage)}`} alt="default-image" />
              );
            } else {
              return defaultStoryElement();
            }
          };

          switch (element.subtype) {
            case "image-gallery":
              if (element.metadata.type === "gallery") {
                return (
                  <div key={element.id}>
                    {element.type === "composite" ? (
                      element["story-elements"].length === 2 ? (
                        <WithLazy margin="50px">
                          {() => (
                            <CustomImageGalleryElement
                              story={story}
                              element={element}
                              imageAspectRatio={[1, 1]}
                              onClickHandler={(index) => togglePopup(element.id, true, index)}
                            />
                          )}
                        </WithLazy>
                      ) : (
                        <WithLazy margin="50px">
                          {() => (
                            <ImageGalleryElement
                              story={story}
                              element={element}
                              imageAspectRatio={[1, 1]}
                              onClickHandler={(index) => togglePopup(element.id, true, index)}
                            />
                          )}
                        </WithLazy>
                      )
                    ) : (
                      <StoryElement
                        renderTemplates={templates}
                        story={story}
                        element={element}
                        onClick={onCarouselEvent}
                      />
                    )}
                    <div>
                      {showPopup[element.id] ? (
                        <LightBox
                          element={element["story-elements"][galleryItemIndex]}
                          closePopup={() => togglePopup(element.id, false)}
                          publisherLang={publisherAttributes.lang}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              }
              break;

            case "quote":
              // eslint-disable-next-line no-case-declarations
              const elementData = element.text;
              element.text = elementData.replace("@", "");
              return (
                <div
                  styleName="story-element-quote"
                  className="quote-wrapper"
                  data-testid="image-element"
                  key={element.id}
                >
                  <StoryElement
                    renderTemplates={templates}
                    story={story}
                    key={element.id}
                    element={element}
                    externalLink={true}
                    onClick={(e) => onCarouselEvent(e, element)}
                  />
                </div>
              );

            case "blockquote":
              if (element.metadata.attribution.id) {
                const linkedEntities = story["linked-entities"] || [];
                const linkedEntityObj = linkedEntities.find((item) => item.id === element.metadata.attribution.id);
                if (linkedEntityObj) {
                  element.metadata.attribution = { ...linkedEntityObj };
                }
              }

              return <CustomQuoteElement key={element.id} element={element} shareUrl={story.url} />;

            case "references": {
              const referenceObj = get(element, ["story-elements"], []);
              return (
                <div className="reference-link" key={element.id}>
                  {referenceObj.map((obj, index) => (
                    <Link key={index} target="_blank" rel="noopener noreferrer" href={obj.metadata.url}>
                      {" "}
                      <span className="reference-icon">
                        <ExternalLinkIcon />
                      </span>
                      <span className="reference-link-text">{obj.metadata.name}</span>
                    </Link>
                  ))}
                </div>
              );
            }

            case "opinion-poll":
              return <Polltype id={element["polltype-id"]} />;

            case "bigfact":
              return null;

            default:
              return (
                <div
                  className={`${imageElementStyle} ${
                    story["story-template"] === "text" && story["author-name"] === "migrator" ? "left-align" : ""
                  } ${story["story-template"] === "photo" ? "photo-story-element" : ""} ${
                    story["story-template"] === "gallery" ? "photo-story-element" : ""
                  } 
                  ${publisherAttributes.lang}-story-element`}
                  key={element.id}
                >
                  {addWithLazyWrapper()}
                </div>
              );
          }
        })}
    </div>
  );
};

StoryCard.propTypes = {
  card: object,
  story: object,
  index: number,
  template: string,
  onCarouselEvent: func,
  publisherAttributes: object,
  imageElements: array,
};

export default StoryCard;
